import React, { useEffect, useState, useContext } from 'react';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  LoadingButton,
  Dropdown,
  MenuItem,
  FormController,
  FormLabel,
} from '@one-thd/sui-atomic-components';
import { AddressesCount } from '../../shared/addresses-count/addresses-count';
import { ERROR_CODES } from '../../../constants/custom-errors';
import { FORM_MODE } from '../../../models/common/addresses';
import { Alert } from '../../shared/alert/alert';
import {
  ERR_UNABLE_TO_SAVE,
  ERR_GENERIC,
  ERR_NO_SAVED_ADR,
} from '../../../constants/response-error-messages';
import { AddressForm } from '../address-form/address-form';
import { AddressCard } from '../address-card/address-card';
import { useAddresses } from '../../../hooks/use-addresses';
import { Spinner } from '../../shared/spinner/spinner';
import {
  TErrorMessage,
  TSuccessMessage,
} from '../../../models/common/alert-messages';
import {
  ADDED_ADDRESS_MSG,
  DELETED_ADDRESS_MSG,
  EDITED_ADDRESS_MSG,
} from '../../../constants/response-success-messages';
import {
  B2C_ADDRESSES_LIMIT_REACHED_MSG,
  B2B_ADDRESSES_LIMIT_REACHED_MSG,
} from '../../../constants/response-warning-messages';
import {
  ADDRESSES_LIMIT_B2C,
  ADDRESSES_LIMIT_B2B,
} from '../../../constants/general';
import {
  addressSortInputValues,
  AddressSortByLabel,
  addressDefaultSortBy,
} from '../../../constants/sort';
import { generateSortByOptions } from '../../../utils/sort';
import { Pagination } from '../../shared/pagination/pagination';
import {
  pushEventInfo,
  setPageInfo,
  pushErrorEvent,
} from '../../../utils/analytics';
import {
  setDeleteAddressAnalytics,
  setFirstPageAnalytics,
  setPaginatedPageAnalytics,
  setSortAnalytics,
} from '../../../utils/address-analytics';

export const AddressesContent = () => {
  const {
    isB2B,
    addresses,
    addressFormMode,
    getAddresses,
    setAddressFormMode,
    setEditableAddress,
  } = useAddresses();

  const totalPages = Number(addresses?.paginationInfo?.totalPages) || 0;
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const [isLoading, setIsLoading] = useState(false);
  const [deletedAddressIndex, setDeletedAddressIndex] = useState<number | null>(
    null,
  );
  const [successAddAddressMsg, setSuccessAddAddressMsg] =
    useState<TSuccessMessage | null>(null);
  const [editedAddressId, setEditedAddressId] = useState<
    number | undefined | null
  >(null);
  const [errorMessage, setErrorMessage] = useState<TErrorMessage | null>(null);
  const [generalError, setGeneralError] = useState<null | TErrorMessage>(null);
  const [currentSortByValue, setCurrentSortByValue] =
    useState<AddressSortByLabel>(addressDefaultSortBy);
  const [currentPageNumber, setCurrentPageNumber] = useState<number>(1);
  const [newDefaultSuccessMessage, setNewDefaultSuccessMessage] =
    useState<TSuccessMessage | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const { NO_SAVED_ADR_CODE } = ERROR_CODES;

  const addressCount = Number(addresses?.paginationInfo?.totalNumberOfRecords);

  const isB2CAddressLimitReached =
    !isB2B && addressCount === ADDRESSES_LIMIT_B2C;

  const isB2BAddressLimitReached =
    isB2B && addressCount === ADDRESSES_LIMIT_B2B;

  const addressLimitReached =
    isB2CAddressLimitReached || isB2BAddressLimitReached;

  const limitReachedMessage = isB2B
    ? B2B_ADDRESSES_LIMIT_REACHED_MSG.message
    : B2C_ADDRESSES_LIMIT_REACHED_MSG.message;

  const resetActionsState = () => {
    setDeletedAddressIndex(null);
    setErrorMessage(null);
    setSuccessAddAddressMsg(null);
    setEditedAddressId(null);
    setNewDefaultSuccessMessage(null);
  };

  const handleDeleteCardSuccess = (index: number) => {
    resetActionsState();
    setDeletedAddressIndex(index);
    const isLastPage =
      currentPageNumber > 1 && currentPageNumber === totalPages;
    const isLastAddressOnPage =
      addresses?.paginationInfo?.addressesOnCurrentPage === 1;
    const newPage =
      isLastAddressOnPage && isLastPage
        ? currentPageNumber - 1
        : currentPageNumber;
    // ensure user stays on the same page or on the previous page (if last address deleted) to show delete confirmation
    triggerGetAddresses(currentSortByValue, newPage);
    setDeleteAddressAnalytics();
  };

  const handleDeleteCardFailure = () => {
    resetActionsState();
    setErrorMessage(ERR_UNABLE_TO_SAVE);
    pushErrorEvent(
      'address card: delete',
      'inline',
      'n/a',
      ERR_UNABLE_TO_SAVE.message,
    );
  };

  const handleDefaultChangeSuccess = () => {
    resetActionsState();
    triggerGetAddresses(currentSortByValue, 1);
    setNewDefaultSuccessMessage(EDITED_ADDRESS_MSG);
  };

  const handleDefaultChangeFailure = () => {
    resetActionsState();
    setErrorMessage(ERR_UNABLE_TO_SAVE);
    pushErrorEvent(
      'address card: set as default',
      'inline',
      'n/a',
      ERR_UNABLE_TO_SAVE.message,
    );
  };

  const onEditSuccess = (adrId: number) => {
    setEditedAddressId(adrId);
    setAddressFormMode(null);
    setEditableAddress(null);
    // reset addresses to last edited and page 1
    triggerGetAddresses('Last Edited', 1);
  };

  const onCloseEditedSuccessMsg = () => {
    setEditedAddressId(null);
    setNewDefaultSuccessMessage(null);
  };

  const onAddSuccess = () => {
    setAddressFormMode(null);
    setSuccessAddAddressMsg(ADDED_ADDRESS_MSG);
    setDeletedAddressIndex(null);
    // reset addresses to last edited and page 1
    triggerGetAddresses('Last Edited', 1);
  };

  const triggerGetAddresses = (sortBy: AddressSortByLabel, pageNumber = 1) => {
    const isAddressesPageChanged = currentPageNumber !== pageNumber;
    const options = generateSortByOptions(sortBy, { page: pageNumber });

    setErrorCode(null);
    setCurrentSortByValue(sortBy);
    if (isAddressesPageChanged) {
      setCurrentPageNumber(pageNumber);

      if (pageNumber !== 1) {
        setPaginatedPageAnalytics();
      } else {
        setFirstPageAnalytics();
      }
    }
    setIsLoading(true);

    return getAddresses(options)
      .catch((error) => {
        if (error.message === NO_SAVED_ADR_CODE) {
          setErrorCode(NO_SAVED_ADR_CODE);
          return;
        }
        setGeneralError(ERR_GENERIC);
        setPageInfo(
          'error',
          'page',
          'addresses page',
          ERR_GENERIC.message,
          'n/a',
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    triggerGetAddresses(currentSortByValue, currentPageNumber);
    setFirstPageAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showAddAddressForm = () => {
    resetActionsState();
    const event = {
      category: {
        primaryCategory: 'overlay',
      },
      eventInfo: {
        eventName: 'add address',
      },
    };
    pushEventInfo(event);
    setAddressFormMode(FORM_MODE.ADD);
  };

  const DeletedAddressSuccessAlert = (
    <Alert
      status="success"
      key="deletedAddress"
      onClose={() => {
        setDeletedAddressIndex(null);
      }}
      data-testid="success-address-deleted-msg"
    >
      {DELETED_ADDRESS_MSG.message}
    </Alert>
  );

  const EditedAddressSuccessAlert = (
    <div className="sui-mb-8">
      <Alert
        status="success"
        onClose={onCloseEditedSuccessMsg}
        data-testid="success-edited-address-msg"
      >
        {EDITED_ADDRESS_MSG.message}
      </Alert>
    </div>
  );

  const onChangePage = (newPageNumber: number) => {
    resetActionsState();
    triggerGetAddresses(currentSortByValue, newPageNumber);
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  };

  const NoSavedAddressesMsg = (
    <Alert status="info">{ERR_NO_SAVED_ADR.message}</Alert>
  );

  const executeSort = (
    _event: React.MouseEvent,
    sortType: AddressSortByLabel,
  ) => {
    setSortAnalytics(sortType);
    triggerGetAddresses(sortType, 1);
  };

  const getAddressesList = () => {
    const addressList =
      (addresses?.addresses &&
        addresses.addresses.map((address, index) => (
          <div
            key={address.addrIdentifier + '-' + address.lastModifiedDate}
            className={`sui-flex ${isMobile ? 'sui-flex-col' : ''}`}
          >
            <AddressCard
              index={index}
              address={address}
              onDeleteSuccess={handleDeleteCardSuccess}
              onDeleteFailure={handleDeleteCardFailure}
              onCloseSuccessMsg={onCloseEditedSuccessMsg}
              resetActionsState={resetActionsState}
              isShowEditedSuccessMsgMW={
                editedAddressId === address.addrIdentifier ||
                (!!newDefaultSuccessMessage && address.isDefault)
              }
              onDefaultChangeSuccess={handleDefaultChangeSuccess}
              onDefaultChangeFailure={handleDefaultChangeFailure}
            />
          </div>
        ))) ||
      [];

    if (isMobile && deletedAddressIndex !== null)
      addressList.splice(deletedAddressIndex, 0, DeletedAddressSuccessAlert);

    return addressList;
  };

  return !generalError ? (
    <>
      <Spinner active={isLoading} />
      {errorMessage && !isMobile && (
        <Alert status="error">{errorMessage.message}</Alert>
      )}
      {deletedAddressIndex !== null && !isMobile && DeletedAddressSuccessAlert}
      {(editedAddressId || newDefaultSuccessMessage) &&
        !isMobile &&
        EditedAddressSuccessAlert}
      {successAddAddressMsg && (
        <div className="sui-mb-8">
          <Alert
            status="success"
            onClose={() => setSuccessAddAddressMsg(null)}
            test-dataid="success-add-address-msg"
          >
            {successAddAddressMsg.message}
          </Alert>
        </div>
      )}
      {addressFormMode ? (
        <AddressForm
          onAddSuccess={onAddSuccess}
          onEditSuccess={onEditSuccess}
        />
      ) : (
        <>
          {addressLimitReached && (
            <Alert status="warning">{limitReachedMessage}</Alert>
          )}
          <div className="sui-grid sui-mb-2 sui-gap-y-6 sui-gap-x-8 sui-grid-cols-1 md:sui-grid-cols-2 2xl:sui-grid-cols-3">
            <div className="sui-self-end sui-col-span-1 sui-col-start-1">
              <LoadingButton
                onClick={showAddAddressForm}
                fullWidth
                disabled={addressLimitReached}
                data-testid="add-address-form-btn"
              >
                Add Address
              </LoadingButton>
            </div>
            {isB2B && addresses?.addresses.length && (
              <div
                key="sortByForm"
                className="sui-col-span-1 md:sui-col-start-2 2xl:sui-col-start-3"
              >
                <FormController fullWidth>
                  <FormLabel htmlFor="sort-dropdown">Sort By</FormLabel>
                  <Dropdown
                    maxItems={5}
                    id="sort-dropdown"
                    data-testid="sort-dropdown"
                    value={currentSortByValue}
                    onChange={executeSort}
                  >
                    {addressSortInputValues.map((sortBy) => (
                      <MenuItem key={sortBy} value={sortBy}>
                        {sortBy}
                      </MenuItem>
                    ))}
                  </Dropdown>
                </FormController>
              </div>
            )}
          </div>
          {!isLoading && errorCode === NO_SAVED_ADR_CODE && NoSavedAddressesMsg}
          <div className="sui-mt-6">
            <AddressesCount count={addressCount} />
          </div>
          <div
            className="sui-grid sui-grid-cols-1 sui-gap-y-4 sui-mt-2 sm:sui-mt-1 sui-gap-x-8 sm:sui-gap-y-8 md:sui-grid-cols-2 2xl:sui-gap-y-16 2xl:sui-grid-cols-3"
            data-testid="addresses"
          >
            {getAddressesList()}
          </div>
          {totalPages > 1 && (
            <div className="sui-w-full sui-justify-center sui-p-5 sui-flex">
              <Pagination
                nPages={totalPages}
                onChangePage={onChangePage}
                page={currentPageNumber}
              />
            </div>
          )}
        </>
      )}
    </>
  ) : (
    <Alert status="error">{generalError.message}</Alert>
  );
};
